import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  applyFunnelUpload,
  FUNNEL_LABELS,
  funnelLiveUrl,
  type FunnelPageType,
  KOPIERBARE_SEITEN,
  type KopierQuelle,
  kopiereFunnelSeite,
  ladeKopierQuellen,
  type UploadDryRunResponse,
  type UploadItem,
  type UploadReport,
  uploadFunnelDryRun,
} from './api';
import { ConfirmDialog, Loading, WarningList } from './ui';

/**
 * Reiter „Hochladen" (Spec § 1): ZIP, einzelne HTML + lose Zusatzdateien, ganzer Ordner (Dialog per
 * `webkitdirectory` ODER Drag-&-Drop eines Ordners ueber DataTransferItem.webkitGetAsEntry).
 *
 * Ablauf: Trockenlauf (Server transformiert, schreibt nichts) → Bericht → „Uebernehmen" schreibt
 * Entwurf + Version. Fehlende Dateien lassen sich nachreichen (Pfad wird aus dem Bericht
 * vorbelegt, ist editierbar) und der Trockenlauf neu starten.
 *
 * Grenzen setzt der SERVER (45 MB Request, ZIP 500 Eintraege/80 MB entpackt, Pfadfilter) — der
 * Client prueft nur die Request-Groesse mit demselben Wert, damit nginx nicht mit einer
 * HTML-413-Seite antwortet. `accept` bewacht nur den Dateidialog, nie den Drop
 * (.claude/rules/react-frontend.md § accept).
 */

/** Spec § 1: Request ≤ 45 MB (nginx erlaubt 50 MB). */
const MAX_REQUEST_BYTES = 45 * 1024 * 1024;
/** Systemmuell — der Server filtert ihn ebenfalls; hier nur, damit er nicht mitgeschickt wird. */
const MUELL = /(^|\/)(__MACOSX|\.DS_Store|Thumbs\.db)(\/|$)/;

interface Item extends UploadItem {
  /** Aus „Fehlende nachreichen" — Pfad editierbar. */
  nachgereicht?: boolean;
}

const fmtBytes = (n: number) =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

const basename = (p: string) => {
  const ohneQuery = p.split(/[?#]/)[0];
  return ohneQuery.slice(ohneQuery.lastIndexOf('/') + 1);
};

/** Stabiler React-Key je File-Objekt — der Pfad taugt nicht, er ist bei nachgereichten Dateien editierbar. */
const FILE_KEYS = new WeakMap<File, number>();
let fileKeySeq = 0;
function fileKey(f: File): number {
  let k = FILE_KEYS.get(f);
  if (k === undefined) {
    fileKeySeq += 1;
    k = fileKeySeq;
    FILE_KEYS.set(f, k);
  }
  return k;
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  // readEntries liefert in Haeppchen (Chrome: 100) — bis zur leeren Antwort weiterlesen.
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const next = () =>
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(all);
          return;
        }
        all.push(...batch);
        next();
      }, reject);
    next();
  });
}

async function collectEntry(entry: FileSystemEntry, out: Item[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    out.push({ file, path: entry.fullPath.replace(/^\/+/, '') });
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllEntries((entry as FileSystemDirectoryEntry).createReader());
    for (const child of children) await collectEntry(child, out);
  }
}

function mergeItems(prev: Item[], add: Item[]): Item[] {
  const map = new Map(prev.map((it) => [it.path, it]));
  for (const it of add) {
    if (!MUELL.test(it.path)) map.set(it.path, it);
  }
  return Array.from(map.values());
}

/** Verzeichnis der HTML-Datei innerhalb der Quelle ('index-2/index.html' → 'index-2/'). */
const htmlDir = (report: UploadReport | null) => {
  const h = report?.quelle.htmlDatei || '';
  return h.includes('/') ? h.slice(0, h.lastIndexOf('/') + 1) : '';
};

/** Vorgeschlagener Upload-Pfad fuer eine nachgereichte Datei: der fehlende Pfad mit passendem Dateinamen. */
function vorschlagPfad(file: File, report: UploadReport | null): string {
  const treffer = report?.fehlend.find((f) => basename(f) === file.name);
  if (!treffer) return file.name;
  if (/^[a-z]+:\/\//i.test(treffer)) return file.name;
  const rel = treffer.replace(/^\.?\//, '');
  return `${htmlDir(report)}${rel}`;
}

interface Props {
  webinarId: string;
  pageType: FunnelPageType;
  /** Seite existiert schon → „Übernehmen" ersetzt den Entwurf (alte Fassung bleibt als Version). */
  pageExists: boolean;
  onApplied: (label: string, warnings: string[]) => void;
  onDirtyChange: (dirty: boolean) => void;
}

export default function UploadPanel({
  webinarId,
  pageType,
  pageExists,
  onApplied,
  onDirtyChange,
}: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [dryRun, setDryRun] = useState<UploadDryRunResponse | null>(null);
  const [busy, setBusy] = useState<'dry' | 'apply' | 'drop' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLInputElement>(null);
  const nachRef = useRef<HTMLInputElement>(null);

  // `webkitdirectory` kennt React nicht als Prop — als Attribut setzen.
  useEffect(() => {
    dirRef.current?.setAttribute('webkitdirectory', '');
    dirRef.current?.setAttribute('directory', '');
  }, []);

  const totalBytes = useMemo(() => items.reduce((s, it) => s + it.file.size, 0), [items]);
  const tooBig = totalBytes > MAX_REQUEST_BYTES;
  const dirty = items.length > 0 || !!dryRun;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  /** Jede Aenderung an der Dateiliste macht einen vorhandenen Bericht ungueltig. */
  const changeItems = (fn: (prev: Item[]) => Item[]) => {
    setItems(fn);
    setDryRun(null);
    setError(null);
  };

  const addFiles = (list: FileList | null, mode: 'lose' | 'ordner' | 'nach') => {
    if (!list || !list.length) return;
    const add: Item[] = Array.from(list).map((file) => {
      if (mode === 'ordner') return { file, path: file.webkitRelativePath || file.name };
      if (mode === 'nach')
        return { file, path: vorschlagPfad(file, dryRun?.report ?? null), nachgereicht: true };
      return { file, path: file.name };
    });
    changeItems((prev) => mergeItems(prev, add));
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    // Eintraege SYNCHRON abgreifen: nach dem ersten await ist die DataTransfer-Liste leer.
    const entries: FileSystemEntry[] = [];
    const loose: File[] = [];
    for (const item of Array.from(e.dataTransfer.items || [])) {
      if (item.kind !== 'file') continue;
      const entry = item.webkitGetAsEntry?.();
      if (entry) entries.push(entry);
      else {
        const f = item.getAsFile();
        if (f) loose.push(f);
      }
    }
    if (!entries.length && !loose.length) {
      for (const f of Array.from(e.dataTransfer.files || [])) loose.push(f);
    }
    setBusy('drop');
    try {
      const out: Item[] = loose.map((file) => ({ file, path: file.name }));
      for (const entry of entries) await collectEntry(entry, out);
      changeItems((prev) => mergeItems(prev, out));
    } catch (err) {
      setError(`Ordner konnte nicht gelesen werden: ${(err as Error)?.message || String(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const runDry = async () => {
    if (!items.length || tooBig) return;
    setBusy('dry');
    setError(null);
    setDryRun(null);
    const res = await uploadFunnelDryRun(webinarId, pageType, items);
    setBusy(null);
    if (!res.success || !res.data) {
      setError(res.error || 'Prüflauf fehlgeschlagen');
      return;
    }
    setDryRun(res.data);
  };

  const apply = async () => {
    if (!dryRun) return;
    setConfirmApply(false);
    setBusy('apply');
    setError(null);
    const res = await applyFunnelUpload(webinarId, pageType, dryRun.uploadToken);
    setBusy(null);
    if (!res.success || !res.data) {
      setError(
        `${res.error || 'Übernehmen fehlgeschlagen'} — bei abgelaufenem Prüflauf bitte „Erneut prüfen“ und dann übernehmen.`,
      );
      return;
    }
    const label = `${FUNNEL_LABELS[pageType]} aus Upload übernommen (Version ${res.data.versionId})${
      res.data.bestandVersionId
        ? ` — alter Stand gesichert als Version ${res.data.bestandVersionId}`
        : ''
    }`;
    setItems([]);
    setDryRun(null);
    onApplied(label, [...(res.data.warnings || []), ...(res.data.report?.warnungen || [])]);
  };

  const report = dryRun?.report ?? null;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SeiteBrauchtHinweis pageType={pageType} />
      <Alert severity="info">
        Ziel: <b>{FUNNEL_LABELS[pageType]}</b>.{' '}
        {pageExists
          ? 'Übernehmen ersetzt den Entwurf — der bisherige Stand bleibt als Version erhalten. Ins Netz geht er erst mit dem nächsten Veröffentlichen der Strecke.'
          : 'Die Seite wird mit dem Upload angelegt.'}
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}

      {KOPIERBARE_SEITEN.includes(pageType) && (
        <SeiteUebernehmen
          webinarId={webinarId}
          pageType={pageType}
          pageExists={pageExists}
          gesperrt={!!busy || items.length > 0}
          onApplied={onApplied}
        />
      )}

      <Paper
        variant="outlined"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        sx={{
          p: 3,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragOver ? 'primary.main' : 'divider',
          backgroundColor: dragOver ? 'action.hover' : 'transparent',
        }}
      >
        <Typography variant="body2" gutterBottom>
          ZIP, HTML-Datei samt Bildern/CSS oder einen ganzen Ordner hier ablegen
        </Typography>
        <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => fileRef.current?.click()}
            disabled={!!busy}
          >
            Dateien wählen (ZIP / HTML + Zusatzdateien)
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => dirRef.current?.click()}
            disabled={!!busy}
          >
            Ordner wählen
          </Button>
        </Box>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files, 'lose');
            e.target.value = '';
          }}
        />
        <input
          ref={dirRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files, 'ordner');
            e.target.value = '';
          }}
        />
        <input
          ref={nachRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files, 'nach');
            e.target.value = '';
          }}
        />
        {busy === 'drop' && <Loading label="Ordner wird gelesen …" />}
      </Paper>

      {items.length > 0 && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="subtitle2">
              {items.length} Datei{items.length === 1 ? '' : 'en'} · {fmtBytes(totalBytes)}
            </Typography>
            <Button size="small" onClick={() => changeItems(() => [])} disabled={!!busy}>
              Liste leeren
            </Button>
          </Box>
          {tooBig && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Zusammen {fmtBytes(totalBytes)} — der Server nimmt höchstens 45 MB je Upload. Große
              Dateien (Videos, ungenutzte Bilder) weglassen.
            </Alert>
          )}
          <Box
            sx={{
              maxHeight: 220,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {items.map((it) => (
              <Box
                key={`${fileKey(it.file)}:${it.nachgereicht ? 'n' : it.path}`}
                display="flex"
                alignItems="center"
                gap={1}
                px={1}
                py={0.25}
              >
                {it.nachgereicht ? (
                  <TextField
                    size="small"
                    variant="standard"
                    value={it.path}
                    onChange={(e) => {
                      const neu = e.target.value;
                      changeItems((prev) => prev.map((p) => (p === it ? { ...p, path: neu } : p)));
                    }}
                    helperText="Pfad relativ zur Quelle — muss zum fehlenden Verweis passen"
                    sx={{ flex: 1 }}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}
                  >
                    {it.path}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {fmtBytes(it.file.size)}
                </Typography>
                <IconButton
                  size="small"
                  title="Entfernen"
                  onClick={() => changeItems((prev) => prev.filter((p) => p !== it))}
                  disabled={!!busy}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Box display="flex" gap={1} mt={1} alignItems="center">
            <Button variant="contained" onClick={runDry} disabled={!!busy || tooBig}>
              {busy === 'dry' ? 'Prüft …' : report ? 'Erneut prüfen' : 'Prüflauf starten'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Der Prüflauf schreibt nichts — erst „Übernehmen“ legt Entwurf und Version an.
            </Typography>
          </Box>
        </Box>
      )}

      {busy === 'dry' && <Loading label="Quelle wird umgebaut und geprüft …" />}

      {report && (
        <UploadReportView
          report={report}
          onNachreichen={() => nachRef.current?.click()}
          onApply={() => setConfirmApply(true)}
          busy={!!busy}
        />
      )}

      <ConfirmDialog
        open={confirmApply}
        title={`Upload für ${FUNNEL_LABELS[pageType]} übernehmen?`}
        confirmLabel="Übernehmen"
        busy={busy === 'apply'}
        onConfirm={apply}
        onCancel={() => setConfirmApply(false)}
      >
        <Typography variant="body2">
          {pageExists
            ? 'Der Entwurf wird ersetzt; der bisherige Stand bleibt als Version und lässt sich wiederherstellen.'
            : 'Die Seite wird als Entwurf angelegt.'}{' '}
          Die benutzten Dateien werden dabei hochgeladen. Öffentlich wird nichts — das geschieht
          erst über die Schritte „Prüfen“ und „Veröffentlichen“.
        </Typography>
        {report && teileFehlende(report).nachreichen.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {teileFehlende(report).nachreichen.length} Datei(en) fehlen — sie fehlen dann auch auf der
            Seite (an ihrer Stelle bleibt ein leeres Bild).
          </Alert>
        )}
      </ConfirmDialog>
    </Box>
  );
}

/** Marker-Schreibweisen (node-app/src/services/lpTemplates.ts ORDER_FORM_MARKER u. a., marker.ts). */
/**
 * „Oder: aus früherem Webinar übernehmen" (Mirko 23.10.) — spart das erneute Hochladen derselben Datei
 * (gemessen: Upsell von FviSD8, VpC6i1, L9GkX8 war byte-gleich dreimal hochgeladen). Übernommen wird der
 * VERÖFFENTLICHTE Stand der Quelle; geschrieben wird wie ein Upload (Entwurf + Version), öffentlich erst über
 * Prüfen → Veröffentlichen.
 */
function SeiteUebernehmen({
  webinarId,
  pageType,
  pageExists,
  gesperrt,
  onApplied,
}: {
  webinarId: string;
  pageType: FunnelPageType;
  pageExists: boolean;
  /** Ein Upload ist gerade in Arbeit (Dateien gewählt oder Prüflauf) — dann nicht parallel übernehmen. */
  gesperrt: boolean;
  onApplied: (label: string, warnings: string[]) => void;
}) {
  const [quellen, setQuellen] = useState<KopierQuelle[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [wahl, setWahl] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setQuellen(null);
    setLadeFehler(null);
    setWahl('');
    void ladeKopierQuellen(pageType, webinarId).then((res) => {
      if (cancelled) return;
      if (res.success && Array.isArray(res.data)) setQuellen(res.data);
      else setLadeFehler(res.error || 'Vorlagen konnten nicht geladen werden');
    });
    return () => {
      cancelled = true;
    };
  }, [pageType, webinarId]);

  const gewaehlt = quellen?.find((q) => q.hash === wahl) ?? null;
  const label = FUNNEL_LABELS[pageType];

  const uebernehmen = async () => {
    if (!gewaehlt) return;
    const quelle = gewaehlt.webinarIds[0];
    setBusy(true);
    setFehler(null);
    const res = await kopiereFunnelSeite(webinarId, pageType, quelle);
    setBusy(false);
    setConfirm(false);
    if (!res.success || !res.data) {
      setFehler(res.error || 'Übernehmen fehlgeschlagen');
      return;
    }
    onApplied(
      `${label} aus ${quelle} übernommen (Version ${res.data.versionId})${
        res.data.bestandVersionId ? ` — alter Stand gesichert als Version ${res.data.bestandVersionId}` : ''
      }`,
      res.data.warnings || [],
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Oder: {label}-Seite aus einem früheren Webinar übernehmen
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Statt dieselbe Datei noch einmal hochzuladen. Übernommen wird der veröffentlichte Stand der gewählten
        Seite; danach wie gewohnt prüfen und veröffentlichen.
        {pageType === 'thanks' &&
          ' Danke-Seiten nennen oft den Report des Webinars — nach dem Übernehmen den Text prüfen.'}
      </Typography>
      {ladeFehler && <Alert severity="error">{ladeFehler}</Alert>}
      {fehler && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {fehler}
        </Alert>
      )}
      {quellen === null && !ladeFehler && <Loading label="Vorlagen laden …" />}
      {quellen?.length === 0 && (
        <Typography variant="body2">
          Keine veröffentlichte {label}-Seite aus anderen Webinaren vorhanden.
        </Typography>
      )}
      {!!quellen?.length && (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Vorlage"
            value={wahl}
            onChange={(e) => setWahl(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 360, flex: 1 }}
            disabled={busy || gesperrt}
          >
            <MenuItem value="">Bitte wählen …</MenuItem>
            {quellen.map((q) => (
              <MenuItem key={q.hash} value={q.hash}>
                {q.headline || '(ohne Überschrift)'} —{' '}
                {q.webinare.map((w) => `${w.titel} (${w.webinar_id})`).join(' / ')}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant="outlined"
            component="a"
            disabled={!gewaehlt}
            href={gewaehlt ? funnelLiveUrl(gewaehlt.webinarIds[0], pageType) : '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ansehen
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!gewaehlt || busy || gesperrt}
            onClick={() => (pageExists ? setConfirm(true) : void uebernehmen())}
          >
            {busy ? 'Übernimmt …' : 'Übernehmen'}
          </Button>
        </Box>
      )}
      <ConfirmDialog
        open={confirm}
        title={`${label}-Seite aus ${gewaehlt?.webinarIds[0] ?? ''} übernehmen?`}
        confirmLabel="Übernehmen"
        busy={busy}
        onConfirm={() => void uebernehmen()}
        onCancel={() => setConfirm(false)}
      >
        <Typography variant="body2">
          Der Entwurf dieser Seite wird ersetzt; der bisherige Stand bleibt als Version und lässt sich
          wiederherstellen. Öffentlich wird nichts — das geschieht erst über „Prüfen“ und „Veröffentlichen“.
        </Typography>
      </ConfirmDialog>
    </Paper>
  );
}

const MARKER_FORMULAR = '<div data-lp-order-form></div>';
const MARKER_COUNTDOWN = '<div data-lp-countdown></div>';
const MARKER_UPSELL = '<div data-lp-upsell></div>';

/**
 * Was eine Seite braucht — VOR dem Hochladen (Plan 2026-10-22: Personas erfuhren erst im
 * HTML-Reiter, dass ihre Seite einen Formular-Marker braucht).
 */
export function SeiteBrauchtHinweis({ pageType }: { pageType: FunnelPageType }) {
  const code = (t: string) => (
    <Box component="code" sx={{ fontSize: 12, bgcolor: '#f3f4f6', px: 0.5, borderRadius: 0.5 }}>
      {t}
    </Box>
  );
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfcfe' }} data-testid="seite-braucht">
      <Typography variant="subtitle2" gutterBottom>
        Bevor du hochlädst — gerade gewählt: {FUNNEL_LABELS[pageType]}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Was jede Seite braucht (deine Seite ist hervorgehoben):
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
        {(
          [
            {
              t: 'order' as const,
              text: (
                <>
                  <b>Pflicht:</b> genau einmal der Platz für das Bestellformular —{' '}
                  {code(MARKER_FORMULAR)} oder {code('{orderform}')}; ohne ihn lässt sich die
                  Strecke nicht veröffentlichen. <b>Optional:</b> der Platz für den Countdown —{' '}
                  {code(MARKER_COUNTDOWN)}.
                </>
              ),
            },
            {
              t: 'upsell' as const,
              text: (
                <>
                  <b>Pflicht:</b> genau einmal der Platz für das Upsell-Formular (Ja/Nein) —{' '}
                  {code(MARKER_UPSELL)} oder {code('{orderform}')}; ohne ihn lässt sich die Strecke
                  nicht veröffentlichen.
                </>
              ),
            },
            {
              t: 'thanks' as const,
              text: <>Kein Formular-Platz nötig — nur der Inhalt der Danke-Seite.</>,
            },
          ]
        ).map(({ t, text }) => (
          <li key={t}>
            <Typography
              variant="body2"
              sx={
                t === pageType
                  ? { bgcolor: '#eef4fc', px: 0.5, borderRadius: 0.5 }
                  : { color: 'text.secondary' }
              }
            >
              <b>{FUNNEL_LABELS[t]}:</b> {text}
            </Typography>
          </li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
        Für alle Seiten:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
        <li>
          <Typography variant="body2">
            <b>Ein Upload = eine Seite.</b> Der Upload nimmt die <code>index.html</code> auf
            oberster Ebene (oder die einzige HTML-Datei). Liegen mehrere Seiten in einem ZIP, wird
            nur die <code>index.html</code> übernommen; gibt es keine, lehnt der Upload ab. Enthält
            das ZIP vom Grafiker alle drei Seiten, bitte drei getrennte anfordern (oder je Seite den
            eigenen Ordner hochladen) — oben die Seite wählen, dann hochladen.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Alle Bilder, CSS-Dateien und Schriften, die die Seite benutzt, mit hochladen — am
            einfachsten als ZIP oder ganzer Ordner. Was fehlt, fehlt später auch auf der Seite.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Liegt im ZIP alles in einem gemeinsamen Ordner, ist das in Ordnung — der Ordner wird
            abgeschnitten.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Grenzen: höchstens 45 MB je Upload; ein ZIP darf entpackt höchstens 80 MB und 500
            Dateien haben (ein Ordner ebenso höchstens 500 Dateien).
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Verweise auf andere Server (z. B. Schriften von Google Fonts) bleiben als Link stehen und
            erscheinen im Prüfbericht unter „Fremde Hosts“; mit hochgeladen werden nur Dateien aus
            dem Upload.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Skripte, iframes und Tracking-Codes werden entfernt — Messung und Einwilligung kommen
            automatisch.
          </Typography>
        </li>
      </Box>
    </Paper>
  );
}

/**
 * Fehlende Dateien nach Einstufung des Servers trennen. Ohne `fehlendDetails` (aelterer Bericht)
 * oder fuer einen Pfad ohne Eintrag gilt „nachreichen" — im Zweifel lieber eine Datei zu viel
 * anfordern als eine benutzte verschweigen.
 */
export function teileFehlende(report: Pick<UploadReport, 'fehlend' | 'fehlendDetails'>): {
  nachreichen: string[];
  ungenutzt: string[];
} {
  const klasse = new Map((report.fehlendDetails || []).map((d) => [d.pfad, d.klasse]));
  const nachreichen: string[] = [];
  const ungenutzt: string[] = [];
  for (const f of report.fehlend) {
    (klasse.get(f) === 'ungenutzte_regel' ? ungenutzt : nachreichen).push(f);
  }
  return { nachreichen, ungenutzt };
}

function ListBlock({
  title,
  items,
  severity,
  hint,
}: {
  title: string;
  items: string[];
  severity: 'warning' | 'info' | 'error';
  hint?: string;
}) {
  if (!items.length) return null;
  return (
    <Alert severity={severity}>
      <b>
        {title} ({items.length})
      </b>
      {hint && (
        <Typography variant="caption" display="block">
          {hint}
        </Typography>
      )}
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 160, overflow: 'auto' }}>
        {items.map((x, i) => (
          <li key={`${i}-${x}`}>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}
            >
              {x}
            </Typography>
          </li>
        ))}
      </Box>
    </Alert>
  );
}

export function UploadReportView({
  report,
  onNachreichen,
  onApply,
  busy,
}: {
  report: UploadReport;
  onNachreichen?: () => void;
  onApply?: () => void;
  busy?: boolean;
}) {
  const m = report.marker;
  const { nachreichen, ungenutzt } = teileFehlende(report);
  const fehltPflichtmarker =
    (report.pageType === 'order' && m.formular === 0) ||
    (report.pageType === 'upsell' && m.widget === 0);
  const markerChips: Array<[string, number]> = [
    ['Formular', m.formular],
    ['Countdown', m.countdown],
    ['Widget', m.widget],
    ['Video', m.vimeo],
    ['Ladebalken', m.progress],
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Prüfbericht: {report.titel || '(ohne Titel)'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Quelle {report.quelle.art.toUpperCase()} · {report.quelle.dateien} Dateien ·{' '}
          {fmtBytes(report.quelle.bytes)}
          {report.quelle.htmlDatei ? ` · HTML: ${report.quelle.htmlDatei}` : ''} · Body{' '}
          {fmtBytes(report.bodyBytes)} · {report.umgezogen.length} Dateien gehen aufs CDN
          {report.kampagne ? ` · Kampagne aus der Quelle: ${report.kampagne}` : ''}
        </Typography>
      </Box>
      <Box display="flex" gap={0.75} flexWrap="wrap">
        {markerChips.map(([label, n]) => (
          <Chip
            key={label}
            size="small"
            label={`${label}: ${n}`}
            variant={n ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      <ListBlock
        title="Fehlende Dateien — nachreichen"
        items={nachreichen}
        severity="warning"
        hint="Die Seite benutzt diese Dateien, sie waren aber nicht im Upload. Übernimmst du so, fehlen sie auf der Seite (leeres Bild an ihrer Stelle). Besser: nachreichen und erneut prüfen."
      />
      <ListBlock
        title="Fehlende Dateien — ungenutzte Regel"
        items={ungenutzt}
        severity="info"
        hint="Nur in Gestaltungsregeln erwähnt, die auf dieser Seite nichts treffen — auf der Seite fehlt dadurch nichts, Nachreichen ist nicht nötig."
      />
      {nachreichen.length > 0 && onNachreichen && (
        <Box>
          <Button size="small" variant="outlined" onClick={onNachreichen} disabled={busy}>
            Fehlende Dateien nachreichen …
          </Button>
        </Box>
      )}
      <ListBlock title="Verworfene iframes" items={report.verworfeneIframes} severity="info" />
      {report.skriptBefunde ? (
        <ListBlock
          title="Skripte entfernt"
          items={report.skriptBefunde.map((b) => `${b.quelle} — ${b.grund}`)}
          severity="info"
          hint={`${report.uebernommeneSkripte ?? 0} Skript(e) übernommen. Entfernt wird, was wir selbst einbinden (Consent, PL-X, Tracking), Datum/Countdown per Skript und Zugriffe auf Formular, Cookies oder fremde Server.`}
        />
      ) : (
        report.verworfeneSkripte > 0 && (
          <Alert severity="info">{report.verworfeneSkripte} Skript(e) entfernt.</Alert>
        )
      )}
      {!!report.skriptBefunde &&
        !report.skriptBefunde.length &&
        (report.uebernommeneSkripte ?? 0) > 0 && (
          <Alert severity="info">{report.uebernommeneSkripte} Skript(e) übernommen.</Alert>
        )}
      <ListBlock title="Tracking-Pixel (übernommen)" items={report.pixel} severity="info" />
      <ListBlock
        title="Fremde Hosts"
        items={report.fremdHosts}
        severity="warning"
        hint="Absolute Verweise auf andere Server — nicht geladen, nur gemeldet."
      />
      <ListBlock title="PL-X-Reste" items={report.plxReste} severity="warning" />
      <ListBlock
        title="Relative Seitenlinks"
        items={report.relativeSeitenlinks}
        severity="warning"
        hint="Links wie /danke/ werden nie umgeschrieben — auf lp.finanztrends.de führen sie ins Leere, bitte prüfen."
      />
      {report.warnungen.length > 0 && (
        <Alert severity="warning">
          <b>Hinweise</b>
          <WarningList warnings={report.warnungen} />
        </Alert>
      )}
      {fehltPflichtmarker && (
        <Alert severity="error">
          {report.pageType === 'order'
            ? 'Kein Platz für das Bestellformular gefunden (Marker fehlt). Ohne ihn lässt sich die Strecke nicht veröffentlichen — im HTML ergänzen und erneut hochladen, oder nach dem Übernehmen unter „Erweitert → HTML“ einfügen.'
            : 'Kein Platz für das Upsell-Formular gefunden (Marker fehlt). Ohne ihn lässt sich die Strecke nicht veröffentlichen.'}
        </Alert>
      )}
      {onApply && (
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button variant="contained" color="success" onClick={onApply} disabled={busy}>
            Übernehmen
          </Button>
          <Typography variant="caption" color={nachreichen.length ? 'warning.main' : 'text.secondary'}>
            {nachreichen.length
              ? `Achtung: ${nachreichen.length} Datei(en) fehlen noch — übernimmst du jetzt, fehlen sie auf der Seite.`
              : 'Keine fehlenden Dateien.'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
